import { useEffect, useMemo, useState } from 'react';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useDoc } from '../../lib/useFirestore';
import { useAuth } from '../../auth/AuthProvider';
import { defaultHelpContent, mergeHelpContent } from '../../lib/helpContent';
import { workflowEnabledFor } from '../../lib/workflowAccess';
import { submitButtonLabel } from '../../lib/matterRecordPdf';

/**
 * Editing the Help page.
 *
 * WHY THIS EXISTS: the Help page's text was written into the component and was wrong within a day
 * — it explained approval chains to a firm whose workflow is switched off. Prose about how the app
 * behaves is configuration, and the people who know what it should say are not the people who
 * deploy.
 *
 * WHAT IS EDITABLE AND WHAT IS NOT. The sections and the questions are text, so they are editable.
 * "What each page is for" is generated from the live form layout and is deliberately not: it has
 * already been wrong once because somebody typed it out, and the only sensible decision left about
 * it is whether to show it at all.
 *
 * AN UNTOUCHED SECTION STILL FOLLOWS THE BUILT-IN TEXT. Only what is actually typed here is
 * stored, so a section left alone corrects itself if the workflow switch or the submit label
 * changes. "Reset" clears the stored text and hands a section back to that behaviour — it is not
 * the same as blanking the boxes, and the panel says so.
 */

const field = 'w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm';
const area = `${field} font-normal leading-relaxed`;

/**
 * Paragraphs are STORED as an array and EDITED as one string, and the conversion happens at the
 * two edges only — never on a keystroke.
 *
 * It used to run on every change, which made it impossible to type a space: the new text was split
 * and trimmed, the trimmed version was joined back up for the textarea's value, and the space you
 * had just pressed vanished before the next character arrived. The draft therefore holds the raw
 * text exactly as typed, and it becomes paragraphs only when it is saved.
 */
const toText = (paragraphs) =>
  Array.isArray(paragraphs) ? paragraphs.join('\n\n') : String(paragraphs || '');
const toParagraphs = (text) =>
  String(text || '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

/** The fields held as raw text while editing and written back as paragraphs. */
const PROSE_FIELDS = new Set(['body', 'a']);

/** Only what the admin has actually changed is saved — the rest keeps following the defaults. */
function storedEntry(entry, defaults, fields) {
  const base = defaults.find((d) => d.id === entry.id);
  const out = { id: entry.id };
  let changed = false;
  for (const key of fields) {
    const mine = entry[key];
    const theirs = base ? base[key] : undefined;
    // Both sides through toText first: the draft holds raw text, the defaults hold paragraphs.
    const same = toText(mine).trim() === toText(theirs).trim();
    if (!base || !same) {
      out[key] = PROSE_FIELDS.has(key) ? toParagraphs(mine) : mine;
      changed = true;
    }
  }
  if (entry.isActive === false) {
    out.isActive = false;
    changed = true;
  }
  // An entry the admin has not touched is still listed, so the ORDER they arranged is kept.
  return changed ? out : { id: entry.id };
}

function Panel({ title, children, onReset, resettable }) {
  return (
    <div className="rounded border border-slate-200 p-3 dark:border-slate-700">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</p>
        {resettable && (
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-slate-500 underline hover:text-firm-700 dark:text-slate-400"
          >
            Reset to the built-in text
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

export default function AdminHelpPage() {
  const { data: stored, loading } = useDoc('settings/helpPage');
  const { data: general } = useDoc('settings/general');
  const { user } = useAuth();
  const { run, busy, error, saved } = useCallable();

  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);

  // The same defaults the page itself uses, so "Reset" restores exactly what a reader would see.
  const defaults = useMemo(() => {
    const workflowEnabled = workflowEnabledFor(general, user?.uid);
    return defaultHelpContent({
      workflowEnabled,
      submitLabel: submitButtonLabel(general, { workflowEnabled }),
    });
  }, [general, user]);

  useEffect(() => {
    if (dirty) return;
    const merged = mergeHelpContent(stored, defaults);
    setDraft({
      ...merged,
      sections: merged.sections.map((entry) => ({ ...entry, body: toText(entry.body) })),
      questions: merged.questions.map((entry) => ({ ...entry, a: toText(entry.a) })),
    });
  }, [stored, defaults, dirty]);

  if (loading || !draft) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;
  }

  const patch = (changes) => {
    setDirty(true);
    setDraft({ ...draft, ...changes });
  };

  const patchList = (key, id, changes) => {
    patch({ [key]: draft[key].map((entry) => (entry.id === id ? { ...entry, ...changes } : entry)) });
  };

  const move = (key, index, direction) => {
    const next = [...draft[key]];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    patch({ [key]: next });
  };

  const resetEntry = (key, id, fields) => {
    const base = (key === 'sections' ? defaults.sections : defaults.questions).find((d) => d.id === id);
    if (!base) return;
    patchList(key, id, Object.fromEntries(fields.map((f) => [f, PROSE_FIELDS.has(f) ? toText(base[f]) : base[f]])));
  };

  const addSection = () =>
    patch({
      sections: [...draft.sections, { id: `firm.help.${Date.now().toString(36)}`, title: 'New section', body: '' }],
    });

  const addQuestion = () =>
    patch({
      questions: [...draft.questions, { id: `firm.faq.${Date.now().toString(36)}`, q: 'New question', a: '' }],
    });

  const remove = (key, id) => patch({ [key]: draft[key].filter((entry) => entry.id !== id) });

  const save = async () => {
    const value = {
      title: draft.title === defaults.title ? '' : draft.title,
      intro: draft.intro === defaults.intro ? '' : draft.intro,
      showPageGuide: draft.showPageGuide !== false,
      sections: draft.sections.map((entry) => storedEntry(entry, defaults.sections, ['title', 'body'])),
      questions: draft.questions.map((entry) => storedEntry(entry, defaults.questions, ['q', 'a'])),
    };
    // Built-in entries the admin deleted are stored as switched off, so they do not simply
    // reappear from the defaults on the next load.
    for (const [key, list] of [['sections', defaults.sections], ['questions', defaults.questions]]) {
      for (const base of list) {
        if (!draft[key].some((entry) => entry.id === base.id)) value[key].push({ id: base.id, isActive: false });
      }
    }
    const result = await run('adminUpdateSettings', { docId: 'helpPage', value });
    if (result) setDirty(false);
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Help page</h2>
          <CallStatus busy={busy} error={error} saved={saved} />
        </div>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          What people read at <strong>Help</strong> in the left rail. Anything you leave alone keeps
          following the built-in text, which already matches how the app is set up — so if the
          approval workflow is switched on later, the parts you have not edited correct themselves.
        </p>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Use <strong>**two asterisks**</strong> around a word or two for bold. Leave a blank line
          between paragraphs.
        </p>

        <label className="mt-3 block text-xs text-slate-600 dark:text-slate-300">
          Page title
          <input className={`${field} mt-1`} value={draft.title} onChange={(e) => patch({ title: e.target.value })} />
        </label>
        <label className="mt-3 block text-xs text-slate-600 dark:text-slate-300">
          Standfirst
          <input className={`${field} mt-1`} value={draft.intro} onChange={(e) => patch({ intro: e.target.value })} />
        </label>

        <label className="mt-3 flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={draft.showPageGuide !== false}
            onChange={(e) => patch({ showPageGuide: e.target.checked })}
          />
          <span>
            Show “What each page is for”
            <span className="block text-xs text-slate-500 dark:text-slate-400">
              Generated from the live form layout, so it always lists the pages people can actually
              see, under your names for them. Not editable here, because a typed-out copy of that
              list is exactly what went wrong before.
            </span>
          </span>
        </label>
      </section>

      <section className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Sections</h3>
          <button type="button" onClick={addSection} className="text-xs text-firm-700 underline dark:text-firm-300">
            + Add a section
          </button>
        </div>
        <div className="mt-3 space-y-3">
          {draft.sections.map((section, index) => (
            <Panel
              key={section.id}
              title={`Section ${index + 1}`}
              resettable={defaults.sections.some((d) => d.id === section.id)}
              onReset={() => resetEntry('sections', section.id, ['title', 'body'])}
            >
              <input
                className={field}
                value={section.title || ''}
                onChange={(e) => patchList('sections', section.id, { title: e.target.value })}
              />
              <textarea
                className={`${area} mt-2`}
                rows={5}
                value={section.body || ''}
                onChange={(e) => patchList('sections', section.id, { body: e.target.value })}
              />
              <div className="mt-2 flex gap-3 text-xs text-slate-500 dark:text-slate-400">
                <button type="button" onClick={() => move('sections', index, -1)} className="underline">
                  Move up
                </button>
                <button type="button" onClick={() => move('sections', index, 1)} className="underline">
                  Move down
                </button>
                <button
                  type="button"
                  onClick={() => remove('sections', section.id)}
                  className="ml-auto text-red-700 underline dark:text-red-300"
                >
                  Remove
                </button>
              </div>
            </Panel>
          ))}
        </div>
      </section>

      <section className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Questions people actually ask
          </h3>
          <button type="button" onClick={addQuestion} className="text-xs text-firm-700 underline dark:text-firm-300">
            + Add a question
          </button>
        </div>
        <div className="mt-3 space-y-3">
          {draft.questions.map((question, index) => (
            <Panel
              key={question.id}
              title={`Question ${index + 1}`}
              resettable={defaults.questions.some((d) => d.id === question.id)}
              onReset={() => resetEntry('questions', question.id, ['q', 'a'])}
            >
              <input
                className={field}
                value={question.q || ''}
                onChange={(e) => patchList('questions', question.id, { q: e.target.value })}
              />
              <textarea
                className={`${area} mt-2`}
                rows={4}
                value={question.a || ''}
                onChange={(e) => patchList('questions', question.id, { a: e.target.value })}
              />
              <div className="mt-2 flex gap-3 text-xs text-slate-500 dark:text-slate-400">
                <button type="button" onClick={() => move('questions', index, -1)} className="underline">
                  Move up
                </button>
                <button type="button" onClick={() => move('questions', index, 1)} className="underline">
                  Move down
                </button>
                <button
                  type="button"
                  onClick={() => remove('questions', question.id)}
                  className="ml-auto text-red-700 underline dark:text-red-300"
                >
                  Remove
                </button>
              </div>
            </Panel>
          ))}
        </div>
      </section>

      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
      >
        Save help page
      </button>
    </div>
  );
}
